import { DiscountTarget } from '@/types/entity/discount';
import { Product } from '@/types/entity/product';
import { resolveTierUnitPrice } from '@/utils/product/resolve-tier-unit-price';
import { DiscountCode } from '@shared/types/cart';
import { LineItem } from '@shared/types/cart/LineItem';
import { Money } from '@shared/types/product/Money';
import { Tier } from '@shared/types/product/Tier';
import { parseCtLifecycleCodes, isStockDueSoonEligibleFromCodes } from '@/utils/product/lifecycle';
import { derivePackSize } from './derive-pack-size';

interface Options {
  discountCodes?: DiscountCode[];
  // Optional fresh per-SKU prices. When provided for an item's SKU,
  // overrides `lineItem.price` — which CT captures at add-to-cart time
  // and never refreshes. The cart server tastic pre-fetches these via
  // `queryPrices` (same path the PDP uses); the client wrapper keeps
  // them fresh across mutations via SWR.
  livePriceBySku?: Record<string, Money | undefined>;
  // EWM-523 — the bulk-pricing tiers that accompany each live base price
  // (`queryPrices` returns `priceBreaks` alongside `price`). When present,
  // the per-unit price is resolved against the line's quantity so the cart
  // shows the same tier price CT charges and the checkout page displays —
  // instead of always the 1+ base price.
  livePriceBreaksBySku?: Record<string, Tier[] | undefined>;
}

export const mapLineItem = (
  lineItem: LineItem,
  { discountCodes = [], livePriceBySku, livePriceBreaksBySku }: Options = {},
): Product => {
  const variant = lineItem.variant;
  const liveBase = variant?.sku ? livePriceBySku?.[variant.sku] : undefined;
  const liveBreaks = variant?.sku ? livePriceBreaksBySku?.[variant.sku] : undefined;
  // Resolve the live base price to the tier matching this line's quantity.
  // With no tiers this is just the base price (unchanged behaviour).
  const liveMoney = resolveTierUnitPrice(liveBase, liveBreaks, lineItem.count ?? 1);

  // EWM-549 — the qty-1 base for the cart's QVB saving callout. `liveBase` is the
  // BFF-resolved qty-1 tier/contract price; `liveMoney`/`item.price` is the tier
  // price applied at this line's quantity (EWM-523). Only emit when the line
  // carries bulk tiers, so non-QVB lines don't gain the field.
  const qvbBaseUnitPrice =
    (liveBreaks?.length ?? 0) > 0 && liveBase?.centAmount != null
      ? liveBase.centAmount / Math.pow(10, liveBase.fractionDigits ?? 2)
      : undefined;

  // Prefer live snapshot price over stale lineItem.price when available.
  const originalPrice = liveMoney?.centAmount ?? lineItem.price?.centAmount ?? variant?.price?.centAmount ?? 0;
  const priceFractionDigits = liveMoney?.fractionDigits ?? lineItem.price?.fractionDigits ?? 2;

  const discountedPrice = lineItem.discountedPrice?.value?.centAmount ?? variant?.discountedPrice?.value?.centAmount;
  const currency = liveMoney?.currencyCode ?? lineItem.price?.currencyCode ?? variant?.price?.currencyCode ?? 'USD';

  const discountCode = discountCodes.find((code) =>
    (code.discounts ?? [])
      .filter(({ target }) => target?.type === 'lineItems')
      .map(({ target }) => target as DiscountTarget)
      ?.find((target) => target.predicate.includes(lineItem.variant?.sku ?? '')),
  );

  let actualDiscountedPrice = discountedPrice;

  // EWM-598 — fall back to the per-unit line total when CT didn't attach a
  // single `discountedPrice` sibling (e.g. cart-discount-driven reductions and
  // free-gift bundles surface via `discountedPricePerCount` / a reduced
  // `totalPrice`). `== null` not falsy so a legitimate $0 line (free gift,
  // scenario #3) is treated as a present discount rather than "no discount".
  if (
    actualDiscountedPrice == null &&
    (lineItem.totalPrice?.centAmount ?? 0) / (lineItem.count ?? 1) < (lineItem.price?.centAmount ?? 0)
  ) {
    actualDiscountedPrice = (lineItem.totalPrice?.centAmount ?? 0) / (lineItem.count ?? 1);
  }

  const discount = lineItem.discountedPrice?.discount ?? variant?.discountedPrice?.discount;
  const discountValue = discount?.discountValue;

  let discountType: 'relative' | 'absolute' | undefined;
  let discountAmount: number | undefined;

  if (discountValue) {
    if (discountValue.type === 'relative') {
      discountType = 'relative';
      discountAmount = discountValue.value / 100;
    } else if (discountValue.type === 'absolute') {
      discountType = 'absolute';
      discountAmount = (discountValue.value?.centAmount ?? 0) / 100;
    }
  }

  // RRP must match what the PDP shows. Primary source is `variant.rrp` (Money),
  // resolved by the BFF from the CT `price-rrp` channel — same source the PDP
  // pricing card reads from. Falls back to the `attributes.rrp` custom attribute
  // (currently unused in electus-dev but kept for forward-compat / mocks).
  const rrpMoney = variant?.rrp;
  const rrpFromMoney =
    typeof rrpMoney?.centAmount === 'number'
      ? rrpMoney.centAmount / Math.pow(10, rrpMoney.fractionDigits ?? 2)
      : undefined;
  const rrpAttrCents = Number(variant?.attributes?.['rrp'] ?? NaN);
  const rrpFromAttr = Number.isFinite(rrpAttrCents) && rrpAttrCents > 0 ? rrpAttrCents / 100 : undefined;
  const rrp = rrpFromMoney ?? rrpFromAttr;
  // FIXME(EWM-202 Gap 1a): attribute key 'minimumOrderQuantity' assumed; confirm with BE once
  // SAP→CT sync spec is finalised. Swap the string key here if the CT attribute name differs.
  const rawMoq = variant?.attributes?.['minimumOrderQuantity'];
  const moq = typeof rawMoq === 'number' && rawMoq > 1 ? rawMoq : undefined;

  // derivePackSize accepts a raw JSON string directly, so no pre-parse needed here.
  const packSize = derivePackSize(variant?.attributes?.['variants.attributes.freightAttributes']);

  // EWM-545 — derive the same "Stock due soon" eligibility the PDP/PLP use, from
  // the same CT `productLifeCycle` source, so the cart line-item label matches.
  // A back-order-allowed customer can add a new/pending (N/P) out-of-stock
  // product; the cart must then read "Stock due soon", not "Out of stock".
  const isStockDueSoonEligible = isStockDueSoonEligibleFromCodes(
    parseCtLifecycleCodes((variant?.attributes ?? {}) as Record<string, unknown>),
  );

  return {
    id: lineItem.lineItemId,
    url: lineItem._url,
    sku: variant?.sku,
    name: lineItem.name,
    description: variant?.attributes?.['Product-Specifications'],
    images: variant?.images,
    quantity: lineItem.count,
    price: originalPrice / Math.pow(10, priceFractionDigits),
    // EWM-598 — emit `discountedPrice` whenever the line carries a reduction
    // strictly below the original, INCLUDING a $0 free-gift line. `!= null` so
    // a legitimate 0 isn't dropped (a falsy check used to hide free gifts).
    ...(actualDiscountedPrice != null && actualDiscountedPrice < originalPrice
      ? { discountedPrice: actualDiscountedPrice / 100 }
      : {}),
    discountPercentage:
      actualDiscountedPrice != null && actualDiscountedPrice < originalPrice && originalPrice > 0
        ? ((originalPrice - actualDiscountedPrice) / originalPrice) * 100
        : 0,
    currency,
    inStock: variant?.isOnStock,
    ...(isStockDueSoonEligible ? { isStockDueSoonEligible } : {}),
    maxQuantity: variant?.isOnStock ? variant.availableQuantity : undefined,
    // EWM-782 — fresh line-coverage flag (EWM-775 stamp, live stock at the last
    // cart mutation). `inStock`/`maxQuantity` above come from the add-time
    // variant snapshot and can be stale; the over-stock gate prefers this.
    ...(typeof lineItem.inStockFlag === 'boolean' ? { stockCovered: lineItem.inStockFlag } : {}),
    ...(moq ? { minimumOrderQuantity: moq } : {}),
    isGift: lineItem.isGift,
    discountCode,
    ...(discount
      ? {
          discount: {
            name: discount.name ?? '',
            description: discount.description ?? '',
            type: discountType,
            value: discountAmount,
          },
        }
      : {}),
    discountsAppliedPerCount: (lineItem.discountedPricePerCount ?? []).map((dpc) => ({
      count: dpc.count ?? lineItem.count,
      details: {
        totalDiscountedPrice: (dpc.discountedPrice?.value.centAmount ?? 0) / 100,
        includedDiscounts: (dpc.discountedPrice?.includedDiscounts ?? []).map((discount) => ({
          discountedAmount: (discount.discountedAmount.centAmount ?? 0) / 100,
          discount: {
            name: discount.discount?.name ?? '',
            description: discount.discount?.description ?? '',
          },
        })),
      },
    })),
    // packSize derived from freightAttributes via derivePackSize (EWM-474).
    // rrp sourced from CT `price-rrp` Standalone Price via variant.rrp Money (same as PDP).
    ...(packSize ? { packSize } : {}),
    ...(rrp !== undefined ? { rrp } : {}),
    ...(qvbBaseUnitPrice !== undefined ? { qvbBaseUnitPrice } : {}),
  } as Product;
};
